<template>
  <div :class="$style['subscribe-form']">
    <div v-if="!result">
      <div v-if="product.type === 'comingsoon'">
        <p>{{ $t('product/subscribe-coming-soon-text') }}</p>
      </div>
      <div v-if="product.type === 'backsoon'">
        <h2>{{ $t('product/subscribe-title') }}</h2>
        <p>{{ $t('product/subscribe-back-soon-text') }}</p>
      </div>

      <div v-if="product.type === 'mistjemaat'">
        <h2>{{ $t('product/size-missing') }}</h2>
        <p>{{ $t('product/fill-in-size-missing-form') }}</p>
      </div>

      <div v-if="shopStock.totalShopStock.stock > 0">
        <p v-if="shopStock.preferredShopStock.stock > 0">
          {{ $t('product/in-stock-store') }} {{ shopStock.preferredShopStock.title }}. <a @click="scrollToStock">{{ $t('product/click-here') }}</a> {{ $t('product/view-stock') }}
        </p>
        <p v-if="shopStock.preferredShopStock.stock === 0">
          {{ $t('product/in-stock-store', {count: shopStock.totalShopStock.titles.length}) }}
          {{ shopStock.totalShopStock.titles.slice(0, shopStock.totalShopStock.titles.length < 3 ? shopStock.totalShopStock.titles.length : 3).join(', ') }}.
          <a @click="scrollToStock">{{ $t('product/click-here') }}</a> {{ $t('product/view-stock') }}
        </p>
      </div>

      <div v-if="product.sizes.length > 1">
        <b>{{ $t('general/select-size') }}</b>
        <div
          :class="$style['subscribe-form__checkboxes']"
          data-test-id="subscribe-form-checkboxes">
          <form-checkbox
            v-for="size in outOfStockSizes"
            :key="size.pId"
            data-test-id="subscribe-form-checkbox"
            :checked="selectedSizes.includes(size.pId)"
            :label="size.size"
            @input="toggleSize(size.pId)" />
        </div>
      </div>
      <form
        v-if="!result"
        @submit.prevent="submitReminder">
        <FormInput
          v-if="!authStore.loggedIn"
          v-model="v$.name.$model"
          class="h-margin-top"
          :class="$style['form-input']"
          data-test-id="subscribe-form-name"
          :validation="v$.name"
          validate
          :label="$t('partials/newsletter-your-name')" />
        <FormInput
          v-if="!authStore.loggedIn"
          v-model="email"
          :validation="v$.email"
          validate
          type="email"
          data-test-id="subscribe-form-email"
          :class="$style['form-input']"
          class="h-margin-top h-margin-bottom"
          :label="$t('partials/newsletter-your-emailaddress')" />

        <FormCheckbox
          :checked="newsletter"
          :target-value="true"
          class="h-margin-bottom"
          :label="$t('product/also-subscribe-to-newsletter')"
          @input="newsletter = !newsletter" />

        <LazyNotificationGroup>
          <LazyNotification
            v-for="(error, n) in errors"
            :key="n">
            {{ error.message }}
          </LazyNotification>
        </LazyNotificationGroup>
        <Btn
          data-test-id="subscribe-form-submit"
          full-width="all"
          color="cta"
          class="h-margin-top">
          {{ $t('product/subscribe-me-now') }}
        </Btn>
      </form>
      <div ref="formBottom" />
    </div>
    <div v-else>
      <h2>{{ $t('general/youre-awesome') }}</h2>
      <p>{{ $t('product/subscribe-success-message') }}</p>
    </div>
  </div>
</template>
<script setup>
import { useVuelidate } from '@vuelidate/core'
import { required, email as emailValidator } from '@vuelidate/validators'
const { $t, $api } = useNuxtApp()

const name = ref(null)
const email = ref(null)
const formBottom = ref(null)
const selectedSizes = ref([])
const result = ref(null)
const errors = ref([])
const newsletter = ref(false)
const userStore = useUserStore()
const authStore = useAuthStore()

const props = defineProps({
  product: {
    type: [Object, Boolean],
    default: null
  },
  size: {
    type: [Object, Boolean],
    default: null
  },
  type: {
    type: [Object, Boolean],
    default: null
  }
})

const rules = {
  name: {
    required
  },
  email: {
    emailValidator,
    required
  }
}

const v$ = useVuelidate(rules, { name, email })

const shopStock = computed(() => {
  const totalShopStock = { titles: [], stock: 0 }
  let preferredShopStock = { title: '', stock: 0 }
  props.product.shopStock.forEach(sizes => {
    if (props.size && props.size.size) {
      if (props.size.size === sizes.size) {
        sizes.shops.forEach(shop => {
          if (shop.name === userStore.preferredShop) {
            preferredShopStock = { title: shop.title, stock: parseInt(shop.stock) }
          } else {
            totalShopStock.stock += parseInt(shop.stock)
            if (shop.stock > 0) { totalShopStock.titles.push(shop.title) }
          }
        })
      }
    } else {
      sizes.shops.forEach(shop => {
        if (shop.name === userStore.preferredShop) {
          preferredShopStock = { title: shop.title, stock: parseInt(shop.stock) }
        } else {
          totalShopStock.stock += parseInt(shop.stock)
          if (shop.stock > 0 && !totalShopStock.titles.includes(shop.title)) {
            totalShopStock.titles.push(shop.title)
          }
        }
      })
    }
  })
  return { totalShopStock, preferredShopStock }
})

const outOfStockSizes = computed(() => {
  return props.product.sizes.filter(size => {
    return size.stock < 1
  })
})

const init = () => {
  if (
    outOfStockSizes.value.length === 1 &&
      !selectedSizes.value.includes(outOfStockSizes.value[0].pId)
  ) {
    selectedSizes.value.push(outOfStockSizes.value[0].pId)
  }
}

onActivated(() => {
  init()
})

onMounted(() => {
  init()
})

const scrollToStock = () => {
  formBottom.value.scrollIntoView({ behavior: 'smooth' })
}

const toggleSize = pId => {
  if (selectedSizes.value.includes(pId)) {
    return selectedSizes.value.splice(selectedSizes.value.indexOf(pId), 1)
  }
  return selectedSizes.value.push(pId)
}

const submitReminder = async () => {
  v$.value.$validate()
  if (v$.value.$invalid && !authStore.loggedIn) { return }
  errors.value = []
  if (!selectedSizes.value.length && props.product.sizes.length > 1) {
    errors.value.push({
      type: 'sizeMissing',
      message: $t('general/select-size')
    })
    return
  }

  try {
    const data = await $api('reminders', {
      method: 'POST',
      body: {
        pIds: selectedSizes.value.length
          ? selectedSizes.value
          : [props.product.sizes[0].pId],
        type: props.product.type,
        name: name.value,
        email: email.value,
        newsletter: newsletter.value
      }
    })

    if (data) {
      result.value = data
    }
  } catch (e) {
    console.log(e.response)
    errors.value = e.response._data.errors
  }
}
</script>
<style lang="scss" module>
.subscribe-form {
  box-shadow: $box-shadow-large;
  border-radius: $border-radius;
  padding: $spacing * 4;
  margin-bottom: $spacing * 2;

  &__checkboxes {
    margin-top: $spacing;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    grid-gap: $spacing;
  }

  a {
    cursor: pointer;
  }
}
</style>
